import React from "react";
import { Dialog, DialogTitle, DialogContent, Box, Typography, Button, IconButton, CircularProgress } from "@mui/material";
import { X, ArrowUpCircle, CheckCircle2 } from "lucide-react";
import { Elements, PaymentElement } from "@stripe/react-stripe-js";
import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";
import { syncSubscriptionAfterPayment } from '../../util/subscription-sync';
import { TierConfig, useSubscriptionTiers } from "../../util/subscription-tiers";
import { usePlatformStripe, useConfirmStripePayment } from "../../util/platform-stripe";
import { usePermissions } from "../../providers/permissions-provider";
import { useUser } from "../../providers/user-provider";
import CompanyAdminsList from "../company-admins-list";

const formatMoney = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

interface UpgradeResult {
    mode: 'initiate' | 'change';
    clientSecret: string | null;
    suggestedRefund?: number;
}

export interface UpgradeRequiredDialogProps {
    open: boolean;
    onClose: () => void;
    entityId: number;
    // From the 403's own body (require-subscription-feature.js /
    // subscription-guard-service.js's requireFeature/requireHorizon) -
    // toTierId is the recommended (cheapest) pick, pre-selected by default;
    // qualifyingTierIds is every plan that would actually satisfy the gate,
    // so the user isn't railroaded into one specific plan (e.g. an annual
    // plan that also covers it, even though it isn't the cheapest).
    toTierId: string;
    qualifyingTierIds?: string[];
    message?: string;
    // Fires once Stripe actually confirms the charge - the entity's tier
    // itself is only promoted once the invoice.paid webhook lands
    // (subscription-webhook-service.js), same as the regular checkout, so
    // this is "go ahead and try the action again in a moment", not
    // "the upgrade is now in effect".
    onUpgraded: () => void;
}

// Covers every case upgradeForFeature's clientSecret can represent - a
// brand-new subscription needing real card details, or an existing
// subscription's charge needing confirmation for any reason (no default
// payment method on file, Stripe requiring 3D Secure, a declined card).
// confirmPayment()/PaymentElement handles all of these correctly on its
// own; guessing the exact PaymentIntent sub-status and picking a narrower
// API per status (as an earlier version of this did with handleNextAction)
// broke the instant a status it didn't anticipate showed up.
// Content for every pay button: the action on the first line and the
// credit notice as a second line INSIDE the button itself - any unused time
// on the current plan is refunded separately, and (Australian consumer law)
// that has to be unmissable at the point of payment.
const PayButtonLabel: React.FC<{ label: string }> = ({ label }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2 }}>
        <span>{label}</span>
        <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 500, opacity: 0.9, mt: 0.4 }}>
            Any unused time on your current plan is credited back separately.
        </Typography>
    </Box>
);

const StripeConfirmStep: React.FC<{ onPaid: (id: string) => void }> = ({ onPaid }) => {
    const { confirm, processing, error, ready } = useConfirmStripePayment(onPaid);
    return (
        <Box sx={{ mt: 2 }}>
            <PaymentElement />
            {error && <Typography sx={{ color: '#e53935', fontSize: 12.5, mt: 1.5 }}>{error}</Typography>}
            <Button
                fullWidth
                variant="contained"
                disabled={!ready || processing}
                onClick={confirm}
                sx={{ mt: 2.5, background: 'var(--color-primary)', color: '#fff', borderRadius: 2, boxShadow: 'none', textTransform: 'none', fontWeight: 700, py: 1.2, '&:hover': { background: 'var(--color-primary-hover)' } }}
            >
                {processing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <PayButtonLabel label="Confirm & Pay" />}
            </Button>
        </Box>
    );
};

const TierOption: React.FC<{ tier: TierConfig; selected: boolean; onSelect: () => void }> = ({ tier, selected, onSelect }) => (
    <Box
        onClick={onSelect}
        sx={{
            p: 1.5,
            borderRadius: 2,
            border: '1.5px solid',
            borderColor: selected ? 'var(--color-primary)' : 'rgba(0,0,0,0.12)',
            backgroundColor: selected ? 'rgba(var(--color-primary-rgb), 0.06)' : 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
        }}
    >
        <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                {tier.name}{tier.billing ? ` - ${tier.billing === 'annual' ? 'Annual' : tier.billing === 'daily' ? 'Daily' : 'Monthly'}` : ''}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                {tier.horizonDays}-day horizon{tier.billing === 'annual' ? ', 12 months up front' : ''}
            </Typography>
        </Box>
        {/* Monthly rate first, as on the plan selection page
            (subscription-plans.tsx), so an annual plan's 12-month total
            isn't read as a monthly price - that total sits underneath. */}
        <Box sx={{ textAlign: 'right', ml: 2, whiteSpace: 'nowrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 0.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{formatMoney(tier.pricePerMonth)}</Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>/month</Typography>
            </Box>
            {tier.billing === 'annual' && (
                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                    {formatMoney(tier.minimumGuaranteed)} / 12 months
                </Typography>
            )}
        </Box>
    </Box>
);

const UpgradeRequiredDialog: React.FC<UpgradeRequiredDialogProps> = ({ open, onClose, entityId, toTierId, qualifyingTierIds, message, onUpgraded }) => {
    const { tiers } = useSubscriptionTiers();
    // Only company admins can pay for an upgrade (routes/subscription.js) -
    // everyone else sees what's needed and is pointed to their admins.
    // Platform admins (role 'master') pass that check too. Waits for the
    // permissions to load (or reload, after switching company) - isAdmin is
    // false until then, which told real admins to "ask an admin".
    const { isAdmin, isLoading: permissionsLoading } = usePermissions();
    const { user } = useUser();
    const canPay = isAdmin || user?.role === 'master';
    const { stripePromise, error: stripeKeyError } = usePlatformStripe();
    const [upgradeResult, setUpgrade] = useApi<UpgradeResult>();
    const [paid, setPaid] = React.useState(false);
    const [selectedTierId, setSelectedTierId] = React.useState(toTierId);

    const options = (qualifyingTierIds?.length ? qualifyingTierIds : [toTierId])
        .map(id => tiers.find(t => t.id === id))
        .filter((t): t is TierConfig => !!t)
        .sort((a, b) => a.minimumGuaranteed - b.minimumGuaranteed);
    const selectedTier = tiers.find(t => t.id === selectedTierId);

    // Fresh every time this is reopened for a (possibly different) gate -
    // without resetting upgradeResult too, a stale 'change' result with no
    // clientSecret would immediately auto-fire the already-charged effect
    // below on reopen, before the user ever clicked Approve again.
    React.useEffect(() => {
        if (open) {
            setPaid(false);
            setUpgrade(null);
            setSelectedTierId(toTierId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, toTierId]);

    const approve = () => {
        const { url, method } = buildApiRequestConfig(routes.subscription.stripeUpgradeForFeature, [
            { param: 'entityId', value: entityId }
        ]);
        setUpgrade({ url, method, body: { tierId: selectedTierId } });
    };

    const handlePaid = () => {
        setPaid(true);
        // Apply the payment now rather than waiting on Stripe's webhook, then
        // let the caller refresh.
        syncSubscriptionAfterPayment(entityId).finally(() => onUpgraded());
    };

    const result = upgradeResult.data;
    const needsConfirmation = !!result?.clientSecret;
    const alreadyCharged = result && !result.clientSecret;

    // Already-charged (mode: 'change', no further action needed) counts as
    // paid immediately - there's nothing left for the user to confirm.
    React.useEffect(() => {
        if (alreadyCharged && !paid) handlePaid();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alreadyCharged]);

    const errorMessage = typeof upgradeResult.error === 'string' ? upgradeResult.error : (upgradeResult.error ? 'Something went wrong starting the upgrade.' : '');

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ArrowUpCircle size={18} color="var(--color-primary)" />
                    <span style={{ fontWeight: 700, fontSize: 18 }}>Upgrade Required</span>
                </Box>
                <IconButton size="small" onClick={onClose}><X size={16} /></IconButton>
            </DialogTitle>
            <DialogContent>
                {paid ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, textAlign: 'center' }}>
                        <CheckCircle2 size={48} color="var(--color-primary)" />
                        <Typography sx={{ mt: 2, fontWeight: 700, fontSize: 17 }}>You're upgraded!</Typography>
                        <Typography sx={{ color: 'var(--color-text-muted)', mt: 1 }}>
                            {selectedTier ? `You're now on the ${selectedTier.name} plan.` : 'Your plan has been upgraded.'} Try that action again.
                        </Typography>
                        {!!result?.suggestedRefund && result.suggestedRefund > 0 && (
                            <Typography sx={{ color: 'var(--color-text-muted)', fontSize: 12.5, mt: 1.5 }}>
                                The unused portion of your previous plan will be credited back separately.
                            </Typography>
                        )}
                        <Button
                            sx={{ mt: 3, background: 'var(--color-primary)', color: '#fff', borderRadius: 2, boxShadow: 'none', textTransform: 'none', fontWeight: 700, px: 4 }}
                            variant="contained"
                            onClick={onClose}
                        >
                            Done
                        </Button>
                    </Box>
                ) : needsConfirmation && result?.clientSecret && stripePromise ? (
                    <Elements stripe={stripePromise} options={{ clientSecret: result.clientSecret, appearance: { theme: 'stripe' } }}>
                        <Typography sx={{ fontSize: 13.5, mb: 1 }}>
                            {selectedTier ? `Upgrading to ${selectedTier.name} - ${formatMoney(selectedTier.minimumGuaranteed)} charged now.` : 'Enter payment details to upgrade.'}
                        </Typography>
                        <StripeConfirmStep onPaid={handlePaid} />
                    </Elements>
                ) : (
                    <Box>
                        <Typography sx={{ fontSize: 13.5, color: 'var(--color-text-muted)' }}>
                            {message || 'This feature needs a higher plan.'}
                        </Typography>
                        {permissionsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={20} sx={{ color: 'var(--color-primary)' }} />
                            </Box>
                        ) : !canPay ? (
                            <>
                                <Typography sx={{ fontSize: 13.5, mt: 2 }}>
                                    Only a company admin can upgrade the plan. Ask an admin of this company to upgrade{selectedTier ? ` to ${selectedTier.name}` : ''}, then try again.
                                </Typography>
                                <CompanyAdminsList entityId={entityId} />
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={onClose}
                                    sx={{ mt: 2.5, background: 'var(--color-primary)', color: '#fff', borderRadius: 2, boxShadow: 'none', textTransform: 'none', fontWeight: 700, py: 1.2, '&:hover': { background: 'var(--color-primary-hover)' } }}
                                >
                                    OK
                                </Button>
                            </>
                        ) : (
                        <>
                        {options.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                {options.map(t => (
                                    <TierOption key={t.id} tier={t} selected={t.id === selectedTierId} onSelect={() => setSelectedTierId(t.id)} />
                                ))}
                            </Box>
                        )}
                        {(errorMessage || stripeKeyError) && (
                            <Typography sx={{ color: '#e53935', fontSize: 12.5, mt: 1.5 }}>{errorMessage || stripeKeyError}</Typography>
                        )}
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={upgradeResult.isLoading || !selectedTier}
                            onClick={approve}
                            sx={{ mt: 2.5, background: 'var(--color-primary)', color: '#fff', borderRadius: 2, boxShadow: 'none', textTransform: 'none', fontWeight: 700, py: 1.2, '&:hover': { background: 'var(--color-primary-hover)' } }}
                        >
                            {upgradeResult.isLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <PayButtonLabel label={selectedTier ? `Upgrade & Pay ${formatMoney(selectedTier.minimumGuaranteed)}` : 'Upgrade'} />}
                        </Button>
                        </>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default UpgradeRequiredDialog;
